import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import {
  businessDate,
  fromJalali,
  toJalali,
  jalaliMonthLength,
  systemClock,
  type BusinessDate,
} from '@sakhtar/calendar';

const WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const JALALI_MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];
// `useGrouping: false` — the Jalali year is 4 digits (e.g. ۱۴۰۵), and
// `toLocaleString('fa-IR')`'s default grouping inserts a thousands
// separator ("٬") into any number ≥ 1000, which reads as broken on a year.
const fa = (n: number) => n.toLocaleString('fa-IR', { useGrouping: false });

/** Persian week starts Saturday. JS getDay() is Sunday=0 — remap by +1 mod 7. */
function persianWeekdayOf(greg: BusinessDate): number {
  const [y, m, d] = greg.split('-').map(Number);
  return (new Date(y!, m! - 1, d!).getDay() + 1) % 7;
}

/** `''` and malformed strings both mean "no date selected" — never throws. */
function tryBusinessDate(value: string): BusinessDate | null {
  if (!value) return null;
  try {
    return businessDate(value);
  } catch {
    return null;
  }
}

export interface JalaliDateInputProps {
  /** `''` or a Gregorian `YYYY-MM-DD` — the same wire format the backend already accepts. Never Jalali on the wire (AGENTS.md §9: dates are calendar-package-only; this is the display boundary, not a second storage format). */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean | undefined;
  style?: CSSProperties;
  /** Passed through to the trigger button for form association. */
  id?: string;
}

/**
 * Jalali/Shamsi date picker over a Gregorian-ISO value — the display
 * boundary the SOT calls for (§9 "Jalali is input/display only through
 * `calendar`"), not a second date format on the wire. Built from the same
 * real month-grid mechanics as TopbarPopovers' QuickCalendarPopover
 * (`toJalali`/`fromJalali`/`jalaliMonthLength`, the one permitted Jalali
 * boundary, ADR-006 §4).
 */
export function JalaliDateInput({
  value,
  onChange,
  placeholder = 'انتخاب تاریخ',
  disabled,
  style,
  id,
}: JalaliDateInputProps) {
  const [open, setOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedG = tryBusinessDate(value);

  const todayG = systemClock.today();
  const todayJ = toJalali(todayG);
  const baseJ = selectedG ? toJalali(selectedG) : todayJ;

  const [viewJy, setViewJy] = useState(baseJ.jy);
  const [viewJm, setViewJm] = useState(baseJ.jm);

  // Re-sync the visible month if the value changes from outside (e.g. form
  // reset). Deliberately depends on the selected date string, not `baseJ`
  // (a derived value recomputed every render, not a stable reference).
  useEffect(() => {
    setViewJy(baseJ.jy);
    setViewJm(baseJ.jm);
  }, [selectedG]);

  useEffect(() => {
    if (!open) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUpwards(spaceBelow < 320 && spaceAbove > spaceBelow);
    }
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (containerRef.current && target && containerRef.current.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const cells = useMemo(() => {
    const leading = persianWeekdayOf(fromJalali({ jy: viewJy, jm: viewJm, jd: 1 }));
    const length = jalaliMonthLength(viewJy, viewJm);
    const out: { label: string; greg: BusinessDate | null }[] = [];
    for (let i = 0; i < leading; i += 1) out.push({ label: '', greg: null });
    for (let jd = 1; jd <= length; jd += 1) {
      out.push({ label: fa(jd), greg: fromJalali({ jy: viewJy, jm: viewJm, jd }) });
    }
    return out;
  }, [viewJy, viewJm]);

  function goMonth(delta: 1 | -1) {
    let jm = viewJm + delta;
    let jy = viewJy;
    if (jm > 12) {
      jm = 1;
      jy += 1;
    }
    if (jm < 1) {
      jm = 12;
      jy -= 1;
    }
    setViewJm(jm);
    setViewJy(jy);
  }

  const displayText = selectedG
    ? `${fa(baseJ.jd)} ${JALALI_MONTH_NAMES[baseJ.jm - 1]} ${fa(baseJ.jy)}`
    : placeholder;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        zIndex: open ? 'var(--sk-z-overlay)' : undefined,
        ...style,
      }}
    >
      <button
        id={id}
        type="button"
        className="sk-input"
        disabled={disabled}
        onClick={() => {
          if (!open && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setOpenUpwards(spaceBelow < 320 && spaceAbove > spaceBelow);
          }
          setOpen((o) => !o);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          width: '100%',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: selectedG ? 'var(--sk-fg)' : 'var(--sk-fg-subtle)',
        }}
      >
        <span className="sk-num">{displayText}</span>
        <Icon name="calendar" size="sm" />
      </button>

      {open ? (
        <div
          className={`sk-datepick${openUpwards ? ' sk-datepick--top' : ''}`}
          style={{
            position: 'absolute',
            top: openUpwards ? undefined : 'calc(100% + 6px)',
            bottom: openUpwards ? 'calc(100% + 6px)' : undefined,
            insetInlineStart: 0,
            width: 272,
            background: 'var(--sk-bg-elev)',
            border: '1px solid var(--sk-border)',
            borderRadius: 'var(--sk-r-lg)',
            boxShadow: 'var(--sk-shadow-3)',
            // Was a bare 60 — below the topbar/bottom-nav's own
            // `--sk-z-sticky` (100), so a field near the top or bottom of
            // the viewport opened a popup rendered visually *behind* the
            // shell chrome (QA-reported "popup does not appear"). The
            // overlay tier sits above every sticky shell element, below
            // modals.
            zIndex: 'var(--sk-z-overlay)',
            padding: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
              {JALALI_MONTH_NAMES[viewJm - 1]} {fa(viewJy)}
            </div>
            <button
              type="button"
              className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
              onClick={() => goMonth(-1)}
            >
              <Icon name="chevronRight" size="sm" />
            </button>
            <button
              type="button"
              className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
              onClick={() => goMonth(1)}
            >
              <Icon name="chevronLeft" size="sm" />
            </button>
          </div>
          <div className="sk-datepick__grid">
            {WEEKDAYS.map((w) => (
              <div key={w} className="sk-datepick__wd">
                {w}
              </div>
            ))}
            {cells.map((c, i) =>
              c.greg === null ? (
                <div key={i} />
              ) : (
                <button
                  key={i}
                  type="button"
                  className={`sk-datepick__day${c.greg === todayG ? ' is-today' : ''}${c.greg === selectedG ? ' is-selected' : ''}`}
                  onClick={() => {
                    onChange(c.greg!);
                    setOpen(false);
                  }}
                >
                  {c.label}
                </button>
              ),
            )}
          </div>
          {selectedG ? (
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  font: 'inherit',
                  fontSize: 12,
                  color: 'var(--sk-fg-subtle)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                پاک کردن تاریخ
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
